// Response shapes as zod schemas: the single source for the API's DTO types
// (types.ts infers from these) and for the OpenAPI document the server
// generates. `.meta({ ref })` names a schema so it appears once under
// components.schemas and is referenced everywhere else.
//
// Shapes follow the desktop contract (desktop/frontend/src/api/types.ts); the
// one deliberate difference is that ids are opaque strings. Dates are ISO
// 8601 strings, calendar days are YYYY-MM-DD, money is a number in dollars.

import { z } from 'zod'
import { CARRIERS, DISCORD_ALERT_KINDS, ORDER_EVENTS, ORDER_STATUSES, PUSH_ENVIRONMENTS, PUSH_PLATFORMS, RETAILERS } from './enums'

export const retailerSchema = z.enum(RETAILERS).meta({ ref: 'Retailer' })
export const orderStatusSchema = z.enum(ORDER_STATUSES).meta({ ref: 'OrderStatus' })
export const carrierSchema = z.enum(CARRIERS).meta({ ref: 'Carrier' })
export const discordAlertKindSchema = z.enum(DISCORD_ALERT_KINDS).meta({ ref: 'DiscordAlertKind' })

// `format: date-time` / `format: date` in the generated spec, so client
// generators map these to real date types. (z.iso.datetime() would add the
// same format plus a long validation regex on every field; these schemas
// describe responses and are never used to validate, so the format alone is
// what clients need.)
const instant = z.string().meta({ format: 'date-time', description: 'ISO 8601 timestamp, UTC' })
const day = z.string().meta({ format: 'date', description: 'Calendar day, YYYY-MM-DD' })
const count = z.number().int()
const imageUrl = z
  .string()
  .optional()
  .describe("Product image. Served from Nebueye's own image cache (public, immutable, cache for a year); load it as is.")
const ratio = z.number().describe('0 to 1')

export const addressSchema = z
  .object({
    recipientName: z.string().optional(),
    addressNumber: z.string().optional(),
    addressStreet: z.string().optional(),
    addressLine2: z.string().optional(),
    city: z.string().optional(),
    state: z.string().optional(),
    zip: z.string().optional(),
  })
  .meta({ ref: 'Address' })

export const orderItemSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    price: z.number(),
    quantity: count,
    sku: z.string().optional(),
    imageUrl,
    productLink: z.string().optional(),
  })
  .meta({ ref: 'OrderItem' })

const orderFields = {
  id: z.string(),
  orderNumber: z.string(),
  email: z.string().describe('The email address the retailer sent the order to'),
  retailer: retailerSchema,
  status: orderStatusSchema,
  shipped: z.boolean(),
  delivered: z.boolean(),
  orderDate: instant,
  updatedAt: instant,
  deliveryDate: z.string().optional().describe('Arrival estimate as the retailer printed it'),
  deliveryStart: day.optional().describe('deliveryDate resolved to a day; end equals start unless a window'),
  deliveryEnd: day.optional(),
  deliveredAt: instant.optional(),
  trackingNumber: z.string().optional(),
  carrier: carrierSchema.optional(),
  cardLastFour: z.string().optional(),
  cardType: z.string().optional(),
  cancelReason: z.string().optional().describe("From a checkout bot's cancel alert; emails never state it"),
  sourceMailbox: z.string().optional().describe('Mailbox the order first arrived in'),
  statusManual: z.boolean().optional(),
  subtotal: z.number().optional(),
  tax: z.number().optional(),
  total: z.number().optional(),
  items: z.array(orderItemSchema),
  address: addressSchema.optional(),
  billing: addressSchema.optional(),
}
export const orderSchema = z.object(orderFields).meta({ ref: 'Order' })

export const statusEventSchema = z
  .object({
    id: z.string(),
    status: orderStatusSchema,
    trackingNumber: z.string().optional(),
    occurredAt: instant,
    manual: z.boolean().optional(),
  })
  .meta({ ref: 'StatusEvent' })

export const discordAlertSchema = z
  .object({
    id: z.string(),
    postedAt: instant,
    source: z.string().describe("Alert format, e.g. 'shikari' or 'prism'"),
    botLabel: z.string().describe('Posting webhook or bot name'),
    kind: discordAlertKindSchema,
    reason: z.string(),
    retailer: retailerSchema.optional(),
    site: z.string(),
    orderNumber: z.string(),
    quantity: count,
    profile: z.string(),
    product: z.string(),
    productUrl: z.string(),
    price: z.number().nullable(),
    email: z.string(),
  })
  .meta({ ref: 'DiscordAlert' })

export const orderDetailSchema = z
  .object({
    ...orderFields,
    statusHistory: z.array(statusEventSchema).describe('Newest first'),
    discordAlerts: z.array(discordAlertSchema).describe('Checkout-bot alerts naming this order, newest first'),
  })
  .meta({ ref: 'OrderDetail' })

export const orderPageSchema = z
  .object({
    orders: z.array(orderSchema),
    total: count.describe('Matches for the full filter, not just this page'),
    retailerCounts: z.partialRecord(z.enum(RETAILERS), count).describe('Matches per retailer with the retailer filter ignored (tab counts)'),
  })
  .meta({ ref: 'OrderPage' })

export const emailAccountSchema = z
  .object({
    id: z.string(),
    email: z.string(),
    retailer: retailerSchema,
    orderCount: count,
    successCount: count,
    failedCount: count,
    emailCount: count,
    lastSubject: z.string().optional(),
    firstSeenAt: instant,
    lastSeenAt: instant,
    sourceMailbox: z.string().optional(),
  })
  .meta({ ref: 'EmailAccount' })

export const emailAccountPageSchema = z.object({ accounts: z.array(emailAccountSchema), total: count }).meta({ ref: 'EmailAccountPage' })

export const dropStatsSchema = z
  .object({
    id: z.string().describe('Stable key for /drops/{id}: retailer, normalized name and start time'),
    productName: z.string(),
    normalizedName: z.string(),
    retailer: retailerSchema,
    dropStartedAt: instant,
    totalOrders: count,
    confirmedOrders: count.describe('Orders that went through (not cancelled or refunded)'),
    cancelledOrders: count,
    shippedOrders: count,
    deliveredOrders: count,
    notYetShipped: count,
    cancelRate: ratio,
    shipRate: ratio,
    deliveryRate: ratio,
    imageUrl,
  })
  .meta({ ref: 'DropStats' })

export const dropOrderSchema = z
  .object({
    email: z.string(),
    orderNumber: z.string(),
    status: orderStatusSchema,
    shipped: z.boolean(),
    delivered: z.boolean(),
    quantity: count,
    total: z.number(),
    orderDate: instant,
    cancelReason: z.string().optional(),
    address: addressSchema.optional(),
    cardType: z.string().optional(),
    cardLastFour: z.string().optional(),
  })
  .meta({ ref: 'DropOrder' })

export const dropSummarySchema = z
  .object({ drops: count, orders: count, kept: count, cancelled: count, shipped: count, delivered: count })
  .meta({ ref: 'DropSummary', description: 'Totals across every drop matching the filter, not just the current page' })

export const dropPageSchema = z
  .object({
    drops: z.array(dropStatsSchema),
    total: count,
    summary: dropSummarySchema,
    retailerCounts: z.partialRecord(z.enum(RETAILERS), count).describe('Drops per retailer with the retailer filter ignored (tab counts)'),
  })
  .meta({ ref: 'DropPage' })

export const dropDetailSchema = z.object({ drop: dropStatsSchema, orders: z.array(dropOrderSchema) }).meta({ ref: 'DropDetail' })

export const dashboardBucketSchema = z
  .object({
    date: instant,
    count,
    kept: count.describe('Orders that went through'),
    cancelled: count,
    spend: z.number().describe('Summed totals of kept orders'),
  })
  .meta({ ref: 'DashboardBucket', description: 'One chart bucket: an hour for the 24h view, otherwise a local day' })

export const dashboardPreviousSchema = z
  .object({ totalOrders: count, keptOrders: count, totalSpend: z.number(), cancelRate: ratio })
  .meta({ ref: 'DashboardPrevious', description: 'The same window shifted back by its own length, for "vs previous" deltas' })

export const retailerPerformanceSchema = z
  .object({ retailer: retailerSchema, orders: count, kept: count, cancelled: count, spend: z.number(), successRate: ratio })
  .meta({ ref: 'RetailerPerformance' })

export const topProductSchema = z
  .object({
    name: z.string(),
    retailer: retailerSchema,
    imageUrl,
    orders: count,
    keptUnits: count.describe('Units across kept orders'),
    spend: z.number(),
  })
  .meta({ ref: 'TopProduct' })

export const fulfilmentPipelineSchema = z
  .object({ awaitingShipment: count, inTransit: count, delivered: count })
  .meta({ ref: 'FulfilmentPipeline', description: 'Where kept orders in the window are in fulfilment right now' })

export const dashboardStatsSchema = z
  .object({
    totalOrders: count,
    keptOrders: count,
    ordersToday: count,
    totalSpend: z.number().describe('Summed totals of kept orders'),
    keptUnits: count.describe('Kept-order units (items), not orders'),
    avgOrderValue: z.number(),
    cancelRate: ratio,
    shipRate: ratio,
    statusCounts: z.record(z.enum(ORDER_STATUSES), count),
    ordersPerDay: z.array(dashboardBucketSchema),
    previous: dashboardPreviousSchema,
    byRetailer: z.array(retailerPerformanceSchema),
    topProducts: z.array(topProductSchema),
    pipeline: fulfilmentPipelineSchema,
    recentDrops: z.array(dropStatsSchema).describe('Most recent drops, newest first'),
  })
  .meta({ ref: 'DashboardStats' })

export const incomingGroupSchema = z
  .object({ date: z.string().describe("Expected day (YYYY-MM-DD), or '' when there is no estimate"), orders: z.array(orderSchema) })
  .meta({ ref: 'IncomingGroup' })

export const incomingViewSchema = z
  .object({
    outForDelivery: z.array(orderSchema),
    deliveredToday: z.array(orderSchema),
    upcoming: z.array(incomingGroupSchema).describe('Shipped, not yet out for delivery, grouped by expected day'),
  })
  .meta({ ref: 'IncomingView' })

export const API_ERROR_CODES = [
  'unauthenticated',
  'account_deleting',
  'forbidden',
  'no_active_workspace',
  'not_found',
  'validation_failed',
  'conflict',
  'rate_limited',
  'internal',
] as const

export const apiErrorBodySchema = z
  .object({
    error: z.object({
      code: z.enum(API_ERROR_CODES),
      message: z.string().describe('Safe to show to the user'),
      requestId: z.string().optional(),
      issues: z.array(z.object({ path: z.string(), message: z.string() })).optional().describe('Field errors for validation_failed'),
    }),
  })
  .meta({ ref: 'ApiErrorBody', description: 'Every non-2xx response from /api/v1 has this body' })

export const deviceInfoSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    platform: z.string(),
    appVersion: z.string(),
    userName: z.string(),
    createdAt: instant,
    lastSeenAt: instant.optional(),
    lastSyncAt: instant.optional(),
    orders: count.describe('Orders this desktop has uploaded'),
  })
  .meta({ ref: 'DeviceInfo', description: 'A connected desktop; its token is never returned' })

export const deviceListSchema = z.object({ devices: z.array(deviceInfoSchema) }).meta({ ref: 'DeviceList' })

export const registeredDeviceSchema = z
  .object({
    deviceId: z.string(),
    token: z.string().describe('Shown once. Send as `Authorization: Bearer <token>` to /api/v1/sync/*'),
    workspaceId: z.string(),
    workspaceName: z.string(),
  })
  .meta({ ref: 'RegisteredDevice' })

export const okSchema = z.object({ ok: z.boolean() }).meta({ ref: 'Ok' })

export const workspaceDataDeletedSchema = z
  .object({
    orders: z.number().int(),
    accounts: z.number().int().describe('Email accounts'),
    alerts: z.number().int(),
    devicesDisconnected: z.number().int().describe('Desktops that were connected; each must sign in again to sync'),
  })
  .meta({ ref: 'WorkspaceDataDeleted', description: 'What was removed from the workspace' })

/** Accounts delete on a 30-day delay; signing in before then and restoring cancels it. */
export const ACCOUNT_DELETION_GRACE_DAYS = 30

export const accountDeleteBody = z.object({
  confirm: z.string().trim().min(1).max(320).describe("The account's email address, typed as a confirmation"),
})

const workspaceRef = z.object({ id: z.string(), name: z.string() })
export const accountDeletionSchema = z
  .object({
    requestedAt: z.iso.datetime().nullable().describe('When deletion was requested; null when the account is not scheduled for deletion'),
    scheduledFor: z.iso.datetime().nullable().describe('When the account and its data are deleted, unless restored before then'),
    workspacesToDelete: z.array(workspaceRef).describe('Workspaces where this user is the only member: deleted with all their data'),
    workspacesToLeave: z.array(workspaceRef).describe('Workspaces with other members: the user is removed and ownership passes to another member'),
  })
  .meta({ ref: 'AccountDeletion', description: "The account's deletion status and what deleting it would remove" })

// Push notifications -------------------------------------------------------

export const orderEventSchema = z.enum(ORDER_EVENTS).meta({ ref: 'OrderEvent' })
export const pushPlatformSchema = z.enum(PUSH_PLATFORMS).meta({ ref: 'PushPlatform' })
export const pushEnvironmentSchema = z.enum(PUSH_ENVIRONMENTS).meta({ ref: 'PushEnvironment' })

export const pushSubscriptionBody = z.object({
  platform: pushPlatformSchema,
  token: z.string().trim().min(16).max(4096).describe('APNs: the device token as lowercase hex'),
  environment: pushEnvironmentSchema
    .default('production')
    .describe('APNs: `sandbox` for Xcode debug builds, `production` for TestFlight and the App Store'),
  deviceName: z.string().trim().max(120).default(''),
  appVersion: z.string().trim().max(40).default(''),
})

export const pushSubscriptionSchema = z
  .object({
    id: z.string().describe('Keep it: DELETE /api/v1/notifications/subscriptions/{id} on sign-out'),
    platform: pushPlatformSchema,
    environment: pushEnvironmentSchema,
    deviceName: z.string(),
    appVersion: z.string(),
    createdAt: instant,
  })
  .meta({ ref: 'PushSubscription', description: 'One phone registered for push; its token is never returned' })

export const notificationPreferencesSchema = z
  .object({
    placed: z.boolean().describe('An order goes through'),
    shipped: z.boolean().describe('An order ships'),
    delivered: z.boolean().describe('A package is delivered'),
    announcements: z.boolean().describe('News from Nebueye (new features). Off until the user turns it on'),
  })
  .meta({ ref: 'NotificationPreferences', description: "Which pushes the user wants, on every phone they're signed in on" })

export const notificationPreferencesBody = notificationPreferencesSchema.partial()

export const pushTestResultSchema = z
  .object({
    subscriptions: count.describe("Phones this user has registered"),
    sent: count.describe('Pushes the push service accepted'),
    mode: z.enum(['send', 'log']).describe('`log`: this server only logs pushes (local dev), nothing reaches a phone'),
  })
  .meta({ ref: 'PushTestResult' })

/** Body of the internal announce call (scripts/announce.ts); not part of the app contract. */
export const announceBody = z.object({
  title: z.string().trim().min(1).max(80),
  body: z.string().trim().min(1).max(400),
  url: z.url().max(2000).optional().describe('Opened when the notification is tapped'),
})
